import React, { useState, useEffect } from 'react'
import {
  Card, Row, Col, DatePicker, Button, Space, Table, Statistic, message, Select, Radio,
  Tag
} from 'antd'
import {
  DownloadOutlined, ReloadOutlined, DollarOutlined, RiseOutlined
} from '@ant-design/icons'
import { analyticsApi, conversationApi } from '@/services/api'
import ReactECharts from 'echarts-for-react'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select

interface CostData {
  group_key: string
  group_name: string
  message_count: number
  total_tokens: number
  total_input_cost: number
  total_output_cost: number
  total_cost: number
  avg_cost_per_message: number
  cost_percentage: number
}

interface CostTrend {
  date: string
  message_count: number
  total_tokens: number
  total_cost: number
}

interface UserCostData {
  user_id: string
  total_conversations: number
  total_messages: number
  total_tokens: number
  total_cost: number
  canvas_adoption_rate: string
  images_usage_rate: string
  avg_cost_per_session: number
  cost_efficiency_score: number
}



const CostAnalysisPage: React.FC = () => {
  const [data, setData] = useState<CostData[]>([])
  const [totalStats, setTotalStats] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)
  const [groupBy, setGroupBy] = useState<'model' | 'provider' | 'date'>('model')
  const [selectedModel, setSelectedModel] = useState<string>('')

  const [costChartData, setCostChartData] = useState<any>(null)
  const [trendChartData, setTrendChartData] = useState<any>(null)



  const fetchData = async () => {
    try {
      setLoading(true)
      const params = {
        startDate: dateRange?.[0]?.valueOf(),
        endDate: dateRange?.[1]?.valueOf(),
        model: selectedModel || undefined,
        groupBy
      }

      // 🔥 修复：增加超时处理和错误重试
      const timeout = 30000; // 30秒超时

      try {
        // 获取基础成本分析数据
        const costResponse = await Promise.race([
          analyticsApi.getCostAnalysis(params),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('成本分析请求超时')), timeout)
          )
        ]) as any;

        if (costResponse.success) {
          setData(costResponse.data.groupedCosts)
          setTotalStats(costResponse.data.totalStats)
          updateCharts(costResponse.data)
        } else {
          message.error('获取成本分析数据失败: ' + (costResponse.error || '未知错误'))
        }
      } catch (error: any) {
        console.error('获取成本分析数据失败:', error)
        message.error('获取成本分析数据失败: ' + (error.message || '请求超时'))
      }

      try {
        // 🔥 处理用户成本数据
        const userResponse = await Promise.race([
          conversationApi.getChatGroups(params),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('用户数据请求超时')), timeout)
          )
        ]) as any;

        if (userResponse.success) {
          // 🔥 修复：确保userData是数组类型，并处理新的数据结构
          const responseData = userResponse.data || {}
          const userData = Array.isArray(responseData.users) ? responseData.users : []
          const processedUserData: UserCostData[] = userData.map((user: any) => ({
            user_id: user.user_id || 'unknown',
            total_conversations: user.conversation_count || 0,
            total_messages: user.message_count || 0,
            total_tokens: user.total_tokens || 0,
            total_cost: user.total_cost || 0,
            canvas_adoption_rate: 0, // 🔥 修复：暂时设为0，因为API数据结构不同
            images_usage_rate: 0, // 🔥 修复：暂时设为0，因为API数据结构不同
            avg_cost_per_session: (user.conversation_count || 0) > 0 ? (user.total_cost || 0) / (user.conversation_count || 0) : 0,
            cost_efficiency_score: calculateCostEfficiency(user)
          }))

          // 处理用户数据但不使用
          console.log('用户数据已处理:', processedUserData.length, '个用户')
        }
      } catch (error: any) {
        console.error('获取用户数据失败:', error)
        // 用户数据失败不影响主要功能，只记录错误
      }

    } catch (error) {
      console.error('获取成本分析数据失败:', error)
      message.error('获取成本分析数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 🔥 计算成本效率分数
  const calculateCostEfficiency = (user: any): number => {
    if (user.total_cost === 0 || user.total_tokens === 0) return 0

    // 基于 Token/成本 比率计算效率分数
    const tokenCostRatio = user.total_tokens / user.total_cost
    const canvasBonus = parseFloat(user.canvas_adoption_rate) * 0.1 // Canvas使用加分
    const imageBonus = parseFloat(user.images_usage_rate) * 0.05 // 图片使用加分

    return Math.min(100, (tokenCostRatio / 1000) * 100 + canvasBonus + imageBonus)
  }



  const updateCharts = (costData: any) => {
    // 更新成本分布图
    if (costData.groupedCosts && costData.groupedCosts.length > 0) {
      const costOption = {
        title: {
          text: `按${groupBy === 'model' ? '模型' : groupBy === 'provider' ? '提供商' : '日期'}的成本分布`,
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params: any) {
            const data = params[0]
            const messageCount = data.data?.message_count || 0
            const costPercentage = data.data?.cost_percentage || 0
            return `${data.name}<br/>
                    消息数: ${messageCount}<br/>
                    总费用: $${(data.value || 0).toFixed(4)}<br/>
                    占比: ${costPercentage.toFixed(2)}%`
          }
        },
        xAxis: {
          type: 'category',
          data: costData.groupedCosts.map((item: CostData) => item.group_name || '未知'),
          axisLabel: {
            rotate: 45,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '费用 ($)'
        },
        series: [
          {
            name: '总费用',
            type: 'bar',
            data: costData.groupedCosts.map((item: CostData) => ({
              value: item.total_cost || 0,
              message_count: item.message_count || 0,
              cost_percentage: item.cost_percentage || 0
            })),
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: '#83bff6' },
                  { offset: 0.5, color: '#188df0' },
                  { offset: 1, color: '#188df0' }
                ]
              }
            }
          }
        ]
      }
      setCostChartData(costOption)
    } else {
      setCostChartData(null)
    }

    // 更新趋势图
    if (costData.costTrends && costData.costTrends.length > 0) {
      const trendOption = {
        title: {
          text: '成本趋势分析',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['每日费用', '消息数'],
          top: 30
        },
        xAxis: {
          type: 'category',
          data: costData.costTrends.map((item: CostTrend) =>
            dayjs(item.date).format('MM-DD')
          )
        },
        yAxis: [
          {
            type: 'value',
            name: '费用 ($)',
            position: 'left'
          },
          {
            type: 'value',
            name: '消息数',
            position: 'right'
          }
        ],
        series: [
          {
            name: '每日费用',
            type: 'line',
            data: costData.costTrends.map((item: CostTrend) => item.total_cost || 0),
            itemStyle: { color: '#1890ff' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
                  { offset: 1, color: 'rgba(24, 144, 255, 0.1)' }
                ]
              }
            }
          },
          {
            name: '消息数',
            type: 'line',
            yAxisIndex: 1,
            data: costData.costTrends.map((item: CostTrend) => item.message_count || 0),
            itemStyle: { color: '#52c41a' }
          }
        ]
      }
      setTrendChartData(trendOption)
    } else {
      setTrendChartData(null)
    }
  }



  const handleExport = async () => {
    try {
      const params = {
        reportType: 'cost_analysis',
        format: 'csv',
        startDate: dateRange?.[0]?.valueOf(),
        endDate: dateRange?.[1]?.valueOf(),
        model: selectedModel || undefined
      }

      await analyticsApi.exportAnalyticsReport(params)
      message.success('导出成功')
    } catch (error) {
      console.error('导出失败:', error)
      message.error('导出失败')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    fetchData()
  }, [dateRange, groupBy, selectedModel])

  const formatCost = (cost: any) => {
    // 🔥 修复：确保输入是数字类型
    const numCost = Number(cost);
    if (isNaN(numCost) || numCost === null || numCost === undefined) {
      return '0.0000'
    }
    return numCost < 0.01 ? numCost.toFixed(6) : numCost.toFixed(4)
  }

  const columns = [
    {
      title: groupBy === 'model' ? '模型信息' : groupBy === 'provider' ? '提供商' : '日期',
      key: 'group_info',
      width: groupBy === 'model' ? 280 : 200,
      render: (_: any, record: any) => {
        if (groupBy === 'model') {
          return (
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                {record.model_name || record.group_name}
              </div>
              <div style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace', marginBottom: 4 }}>
                {record.group_name && record.group_name.length > 35 ? `${record.group_name.substring(0, 35)}...` : record.group_name}
              </div>
              <div style={{ marginTop: 4 }}>
                {record.provider && <Tag color="blue">{record.provider}</Tag>}
                {record.model_type && <Tag color="green">{record.model_type}</Tag>}
                {record.tier && <Tag color="purple">{record.tier}</Tag>}
              </div>
              {/* 🔥 新增：显示模型能力 */}
              <div style={{ marginTop: 4 }}>
                {record.supports_vision && <Tag color="orange">视觉</Tag>}
                {record.supports_reasoning && <Tag color="red">推理</Tag>}
                {record.supports_code && <Tag color="geekblue">代码</Tag>}
                {record.supports_function_calling && <Tag color="cyan">函数</Tag>}
              </div>
            </div>
          )
        }
        return record.group_name
      }
    },
    {
      title: '消息数',
      dataIndex: 'message_count',
      key: 'message_count',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return numValue.toLocaleString();
      }
    },
    {
      title: 'Token总数',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      width: 120,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return numValue.toLocaleString();
      }
    },
    {
      title: '输入费用',
      dataIndex: 'total_input_cost',
      key: 'total_input_cost',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return `$${formatCost(numValue)}`;
      }
    },
    {
      title: '输出费用',
      dataIndex: 'total_output_cost',
      key: 'total_output_cost',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return `$${formatCost(numValue)}`;
      }
    },
    {
      title: '总费用',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 120,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return `$${formatCost(numValue)}`;
      }
    },
    {
      title: '平均费用',
      dataIndex: 'avg_cost_per_message',
      key: 'avg_cost_per_message',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return `$${formatCost(numValue)}`;
      }
    },
    {
      title: '费用占比',
      dataIndex: 'cost_percentage',
      key: 'cost_percentage',
      width: 100,
      align: 'right' as const,
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return `${numValue.toFixed(2)}%`;
      }
    }
  ]

  return (
    <div style={{ padding: '0' }}>
      <Card title="成本分析" extra={
        <Space>
          <Radio.Group value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <Radio.Button value="model">按模型</Radio.Button>
            <Radio.Button value="provider">按提供商</Radio.Button>
            <Radio.Button value="date">按日期</Radio.Button>
          </Radio.Group>
          <Select
            placeholder="选择模型"
            value={selectedModel || undefined}
            onChange={setSelectedModel}
            style={{ width: 200 }}
            allowClear
          >
            <Option value="anthropic/claude-3.7-sonnet">Claude 3.7 Sonnet</Option>
            <Option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku</Option>
            <Option value="anthropic/claude-sonnet-4">Claude Sonnet 4</Option>
            <Option value="openai/gpt-4.1">GPT-4.1</Option>
            <Option value="openai/gpt-4o">GPT-4o</Option>
            <Option value="deepseek/deepseek-r1">DeepSeek R1</Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            placeholder={['开始日期', '结束日期']}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            刷新
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
            导出
          </Button>
        </Space>
      }>
        {/* 统计概览 */}
        {totalStats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Statistic
                title="总记录数"
                value={totalStats.total_records}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="总Token数"
                value={totalStats.total_tokens}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="总费用"
                value={`$${formatCost(Number(totalStats.total_cost) || 0)}`}
                valueStyle={{ color: '#722ed1' }}
                prefix={<DollarOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="平均费用"
                value={`$${formatCost(Number(totalStats.avg_cost_per_message) || 0)}`}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<RiseOutlined />}
              />
            </Col>
          </Row>
        )}

        {/* 图表展示 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card title="成本分布" size="small">
              {costChartData ? (
                <ReactECharts
                  option={costChartData}
                  style={{ width: '100%', height: '350px' }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              ) : (
                <div style={{ width: '100%', height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="成本趋势" size="small">
              {trendChartData ? (
                <ReactECharts
                  option={trendChartData}
                  style={{ width: '100%', height: '350px' }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              ) : (
                <div style={{ width: '100%', height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* 详细数据表格 */}
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="group_key"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  )
}

export default CostAnalysisPage
